import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Table, Button, Card, Popconfirm, Typography, message } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import Layout from "../../ReusableComponents/Layout";
import getReqComp from "../../Redux/APIs/ReqCompanyAPIs/get";
import deleteUser from "../../Redux/APIs/ReqCompanyAPIs/delete";
import updateSatus from "../../Redux/APIs/ReqCompanyAPIs/put";
import getServices from "../../Redux/APIs/ServiceAPIs/get";
import AddServicePopUp from "../../ReusableComponents/AddServicePopUp";
import * as AlertUtils from "../../Utils/alertUtils";
import PaginationComponent from "../../ReusableComponents/Pagination/PaginationComponent";

const { Title, Text } = Typography;

const cardStyle = {
  borderRadius: 12,
  boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)",
};

const StatusPill = ({ added }) => {
  const bg = added ? "#f0fdf4" : "#fffbeb";
  const color = added ? "#15803d" : "#b45309";
  const dot = added ? "#22c55e" : "#f59e0b";
  const label = added ? "Completed" : "Action Needed";
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 10px",
        borderRadius: 10,
        backgroundColor: bg,
        color,
        fontSize: 12,
        fontWeight: 500,
        lineHeight: "20px",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: dot }} />
      {label}
    </span>
  );
};

const RequestedCompanies = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [companyDetails, setCompanyDetails] = useState({});
  const filterUserDeletion = (id) => {
    setUsers((prev) => prev.filter((user) => user._id !== id));
  };

  const handleEmailClick = async (email) => {
    try {
      const userResp = await fetch(
        `${process.env.REACT_APP_BASE_URL}/getUserIdByEmail/${encodeURIComponent(email)}`
      );
      if (userResp.ok) {
        const userResult = await userResp.json();
        navigate(`/user-details/${userResult.data.id}`);
        return;
      }
      if (userResp.status !== 404) {
        message.error("Could not load user details");
        return;
      }

      const guestResp = await fetch(
        `${process.env.REACT_APP_BASE_URL}/getGuestIdByEmail/${encodeURIComponent(email)}`,
        {
          headers: {
            authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (guestResp.ok) {
        const guestResult = await guestResp.json();
        navigate(`/guest-details/${guestResult.data.id}`);
        return;
      }
      if (guestResp.status === 404) {
        message.error("No user or guest found for this email");
        return;
      }
      message.error("Could not load user details");
    } catch {
      message.error("Could not load user details");
    }
  };

  const [page, setPage] = useState(0);
  const [paginationResult, setPaginationResult] = useState({
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 0,
    currentPage: 0,
  });

  useEffect(() => {
    dispatch(getReqComp(setUsers, setPaginationResult, page));
    dispatch(getServices(setServices));
  }, [page]);

  const onAdd = async (company) => {
    try {
      const companyExists = await checkCompanyExists(company.company.name);
      if (companyExists) {
        AlertUtils.CustomErrorAlert("Company already exists");
        dispatch(updateSatus(company._id));
        dispatch(
          getReqComp((data) => {
            const updatedData = data.map((user) => ({ ...user, added: true }));
            setUsers(updatedData);
          })
        );
      } else {
        setShowPopup(true);
        setCompanyDetails(company);
      }
    } catch (error) {
      message.error("Error checking company");
    }
  };

  const checkCompanyExists = async (companyName) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/get-company-by-name?name=${encodeURIComponent(companyName)}`,
        {
          headers: {
            authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const result = await response.json();
      return result.exists;
    } catch {
      return false;
    }
  };

  const columns = [
    {
      title: "Email",
      dataIndex: "userMail",
      key: "userMail",
      ellipsis: true,
      render: (text, record) => (
        <a onClick={() => handleEmailClick(record.userMail)}>
          {text}
        </a>
      ),
    },
    {
      title: "Organization",
      key: "organization",
      render: (_, record) => record?.company?.name,
    },
    {
      title: "Location",
      key: "location",
      ellipsis: true,
      render: (_, record) =>
        `${record?.company?.address?.locality || ""}, ${record?.company?.address?.town || ""}, ${record?.company?.address?.postCode || ""}, ${record?.company?.address?.county || ""}`,
    },
    {
      title: "Status",
      key: "status",
      align: "center",
      width: 130,
      render: (_, record) => <StatusPill added={record?.added} />,
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) =>
        record?.added ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "2px 10px",
              borderRadius: 10,
              backgroundColor: "#f0fdf4",
              color: "#15803d",
              fontSize: 12,
              fontWeight: 500,
            }}
          >
            Added
          </span>
        ) : (
          <Button type="primary" size="small" onClick={() => onAdd(record)} style={{ borderRadius: 6 }}>
            Add
          </Button>
        ),
    },
    {
      title: "",
      key: "delete",
      width: 50,
      render: (_, record) => (
        <Popconfirm
          title="Are you sure you want to delete this request?"
          onConfirm={() => dispatch(deleteUser(record._id, filterUserDeletion))}
          okText="Yes"
          cancelText="No"
        >
          <Button type="text" danger icon={<DeleteOutlined />} style={{ borderRadius: 6 }} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Layout>
      {showPopup && (
        <AddServicePopUp
          services={services}
          company={companyDetails}
          setShowPopup={setShowPopup}
        />
      )}
      <Card style={cardStyle} styles={{ body: { padding: 0 } }}>
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Title level={5} style={{ margin: 0 }}>Requested Companies</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            {paginationResult.totalItems ?? 0} records
          </Text>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="_id"
          pagination={false}
        />

        <PaginationComponent {...paginationResult} setPage={setPage} />
      </Card>
    </Layout>
  );
};

export default RequestedCompanies;
